import { createRoot } from 'react-dom/client';
import * as Sentry from '@sentry/react';
import App from './App.tsx';
import './index.css';
import './i18n'; // Initialize i18n
import '@fontsource/space-grotesk/500.css';
import '@fontsource/space-grotesk/600.css';
import '@fontsource/dm-sans/400.css';
import '@fontsource/dm-sans/500.css';

const APP_BUILD_INFO = {
  build_timestamp: '2026-05-06T18:32:08Z',
  cache_version: 'no-preview-sw-irrigation-decision-report-v2',
  sw_version: 'no-preview-sw-irrigation-decision-report-v2',
  commit_hash_if_available: import.meta.env.VITE_COMMIT_HASH || null,
  irrigation_report_version: 'decision_report_v2',
};

console.log('APP_BUILD_INFO', APP_BUILD_INFO);

if ('serviceWorker' in navigator) {
  navigator.serviceWorker.getRegistrations()
    .then((registrations) => Promise.all(registrations.map((registration) => registration.unregister())))
    .then(() => globalThis.caches?.keys?.() || [])
    .then((names) => Promise.all(names.map((name) => globalThis.caches.delete(name))))
    .then(() => console.log('SERVICE_WORKER_CACHE_CLEANUP', { completed: true, ...APP_BUILD_INFO }))
    .catch((error) => console.warn('SERVICE_WORKER_CACHE_CLEANUP', { completed: false, error: error?.message }));
}

// Initialize Sentry for error monitoring (production only).
// Session Replay is DISABLED to avoid analytics-style cookies that would
// require prior user consent under LSSI Art. 22.2 / AEPD 2023.
// Only error tracking + performance traces are collected (legitimate
// interest, no personal data, no replay cookies).
if (import.meta.env.PROD && import.meta.env.VITE_SENTRY_DSN) {
  Sentry.init({
    dsn: import.meta.env.VITE_SENTRY_DSN,
    environment: import.meta.env.MODE,
    integrations: [
      Sentry.browserTracingIntegration(),
    ],
    tracesSampleRate: 0.1,
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 0,
    beforeSend(event, hint) {
      if (event.exception) {
        const error = hint.originalException as Error;
        if (error?.message?.includes('NetworkError') || error?.message?.includes('Failed to fetch')) {
          return null;
        }
      }
      return event;
    },
  });

  console.log('✅ Sentry initialized (errors only, no session replay)');
} else if (!import.meta.env.VITE_SENTRY_DSN && import.meta.env.PROD) {
  console.warn('⚠️ Sentry DSN not configured. Error monitoring disabled.');
}

// Prevent MetaMask extension from crashing the app with unhandled rejections
window.addEventListener('unhandledrejection', (event) => {
  const msg = event.reason?.message || String(event.reason || '');
  if (msg.includes('MetaMask') || msg.includes('metamask') || msg.includes('inpage.js')) {
    console.warn('MetaMask extension error suppressed:', msg);
    event.preventDefault();
  }
});

createRoot(document.getElementById("root")!).render(<App />);
